// Kinda Katch Living Labs component
import React, { useState, useEffect } from "react";
import NavBar from "../shared/NavBar";
import Footer from "../shared/Footer";
import '../../styles/kindakatch/KindaKatch.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFish, faLeaf, faChartLine, faGraduationCap, faUsers, faArrowRight, faWater, faSeedling, faDatabase, faHandshake } from '@fortawesome/free-solid-svg-icons';

function KindaKatch() {
    const [lightboxImage, setLightboxImage] = useState(null);
    const [activeSection, setActiveSection] = useState(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('show');
                    }
                });
            },
            { threshold: 0.1 }
        );

        const hiddenElements = document.querySelectorAll('.kk-hidden');
        hiddenElements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const openLightbox = (imageSrc) => {
        setLightboxImage(imageSrc);
        document.body.style.overflow = 'hidden';
    };

    const closeLightbox = () => {
        setLightboxImage(null);
        document.body.style.overflow = 'unset';
    };

    const galleryImages = [
        { src: '/images/kinda-katch-hero.jpg', alt: 'Kinda Katch farm site with lined fishponds', caption: 'Kinda Katch Living Labs - Kimuka, Kajiado County' },
        { src: '/images/kinda-katch-pond.jpg', alt: 'Lined fishponds at Kinda Katch', caption: 'Constructed lined fishponds for climate-smart aquaculture' },
        { src: '/images/kinda-katch-stocking.jpg', alt: 'Catfish stocking process', caption: 'First stocking of 2,050 catfish juveniles in October 2026' },
        { src: '/images/kinda-katch-team.jpg', alt: 'Team during fish stocking', caption: 'Team participating in the catfish stocking process' }
    ];

    const innovationAreas = [
        {
            icon: faFish,
            title: 'Climate-Smart Aquaculture',
            description: 'Modular, lined fishponds and practical husbandry methods designed to diversify local food production.'
        },
        {
            icon: faLeaf,
            title: 'Circular Agriculture',
            description: 'Integrating crop cultivation with planned resource-efficient agricultural practices. Bananas and potatoes have been planted as the initiative prepares to assess the safe reuse of nutrient-rich pond water.'
        },
        {
            icon: faChartLine,
            title: 'Digital Farm Intelligence',
            description: 'Using KindaTech ERP and production records to track farm inputs, feed, fish growth, operating costs and production performance.'
        },
        {
            icon: faGraduationCap,
            title: 'Living STEM Learning',
            description: 'Connecting practical agricultural operations with science, technology, engineering, mathematics and climate education.'
        },
        {
            icon: faUsers,
            title: 'Inclusive Green Enterprise',
            description: 'Supporting youth participation, practical skills development, employment opportunities and inclusive entrepreneurship.'
        }
    ];

    const learningAudiences = [
        {
            title: 'Schools',
            description: 'Practical STEM and climate education',
            icon: faGraduationCap
        },
        {
            title: 'TVET Institutions',
            description: 'Technical skills and applied agricultural training',
            icon: faDatabase
        },
        {
            title: 'Universities',
            description: 'Research, innovation and interdisciplinary learning opportunities',
            icon: faSeedling
        },
        {
            title: 'Youth & Community Groups',
            description: 'Practical demonstrations and green enterprise development',
            icon: faUsers
        }
    ];

    const roadmapItems = [
        {
            title: 'Protected Production',
            description: 'Advancing protected agricultural production infrastructure'
        },
        {
            title: 'Aquaculture Expansion',
            description: 'Preparing additional fishpond operations and future production activities'
        },
        {
            title: 'Circular Resource Management',
            description: 'Testing responsible and resource-efficient water-use practices'
        },
        {
            title: 'Market Development',
            description: 'Exploring market pathways for fish sales'
        },
        {
            title: 'Impact Measurement',
            description: 'Documenting production, resource use, educational and economic outcomes to inform responsible expansion'
        }
    ];

    return (
        <div className="kinda-katch-component">
            <NavBar />
            
            {/* Hero Section */}
            <div className="kk-hero-section" style={{ backgroundImage: "url('/images/kinda-katch-hero.jpg')" }}>
                <div className="kk-hero-overlay">
                    <div className="kk-hero-content">
                        <div className="kk-badge">An Initiative of Signifide Group International</div>
                        <h1>Kinda Katch Living Labs</h1>
                        <h2>Growing climate-resilient food systems, practical green skills and new opportunities in Kenya's Maasai drylands.</h2>
                        <p>At Kimuka, Kajiado County, Kinda Katch Living Labs brings climate-smart aquaculture, circular agriculture, digital farm management and hands-on education together at one working demonstration site. An initiative of Signifide Group International, the Living Lab connects food production with STEM learning, youth employment and inclusive green entrepreneurship.</p>
                        <div className="kk-hero-buttons">
                            <button className="kk-primary-btn" onClick={() => scrollToSection('kk-about')}>
                                Explore Kinda Katch
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                            <button className="kk-secondary-btn" onClick={() => scrollToSection('kk-learning')}>
                                Discover Learning Opportunities
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* About Section */}
            <div id="kk-about" className="kk-section kk-hidden">
                <div className="kk-container">
                    <h1>A Living Laboratory for Resilient Drylands</h1>
                    <div className="kk-about-content">
                        <div className="kk-about-text">
                            <p>Kinda Katch is testing an integrated approach to food production in a climate-sensitive environment. The initiative combines lined catfish ponds, emerging protected-production infrastructure, crop cultivation, and ERP-supported farm records to create a practical learning environment.</p>
                            <p>The objective is to understand what works, document costs and outcomes, and develop approaches that can be adapted by other communities.</p>
                            <div className="kk-highlights">
                                <div className="kk-highlight-card">
                                    <FontAwesomeIcon icon={faWater} className="kk-highlight-icon" />
                                    <h3>Location</h3>
                                    <p>Kimuka, Kajiado County, Kenya</p>
                                </div>
                                <div className="kk-highlight-card">
                                    <FontAwesomeIcon icon={faFish} className="kk-highlight-icon" />
                                    <h3>Aquaculture Infrastructure</h3>
                                    <p>Three constructed lined fishponds</p>
                                </div>
                                <div className="kk-highlight-card">
                                    <FontAwesomeIcon icon={faSeedling} className="kk-highlight-icon" />
                                    <h3>First Stocking</h3>
                                    <p>2,050 catfish juveniles stocked in October 2026</p>
                                </div>
                                <div className="kk-highlight-card">
                                    <FontAwesomeIcon icon={faDatabase} className="kk-highlight-icon" />
                                    <h3>Digital Agriculture</h3>
                                    <p>ERP-supported farm management and production records</p>
                                </div>
                            </div>
                        </div>
                        <div className="kk-about-image">
                            <img src="/images/kinda-katch-pond.jpg" alt="Kinda Katch lined fishponds" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Innovation Areas Section */}
            <div className="kk-section kk-alt-bg kk-hidden">
                <div className="kk-container">
                    <h1>Five Connected Areas of Innovation</h1>
                    <p className="kk-section-subtitle">Integrating agriculture, technology, education and enterprise to build resilient communities.</p>
                    <div className="kk-innovation-grid">
                        {innovationAreas.map((area, index) => (
                            <div key={index} className="kk-innovation-card">
                                <FontAwesomeIcon icon={area.icon} className="kk-innovation-icon" />
                                <h3>{area.title}</h3>
                                <p>{area.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Gallery Section */}
            <div className="kk-section kk-hidden">
                <div className="kk-container">
                    <h1>Innovation in Action</h1>
                    <p className="kk-section-subtitle">Discover the people, technology and practical activities behind Kinda Katch Living Labs.</p>
                    <div className="kk-gallery">
                        {galleryImages.map((image, index) => (
                            <div key={index} className="kk-gallery-item" onClick={() => openLightbox(image.src)}>
                                <img src={image.src} alt={image.alt} loading="lazy" />
                                <div className="kk-gallery-overlay">
                                    <FontAwesomeIcon icon={faArrowRight} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Learning Section */}
            <div id="kk-learning" className="kk-section kk-alt-bg kk-hidden">
                <div className="kk-container">
                    <h1>Learning by Doing</h1>
                    <p className="kk-section-subtitle">Transforming real agricultural operations into meaningful educational experiences.</p>
                    <div className="kk-learning-content">
                        <div className="kk-learning-text">
                            <p>Kinda Katch is developing practical learning opportunities that connect agriculture, technology, climate resilience and entrepreneurship.</p>
                            <p>The farm has hosted local learning sessions covering:</p>
                            <ul>
                                <li>Fishpond construction</li>
                                <li>Fish feeding and husbandry</li>
                                <li>Water management</li>
                                <li>Greenhouse development</li>
                                <li>ERP-supported farm management</li>
                                <li>Practical agricultural enterprise skills</li>
                            </ul>
                            <p>Learning resources aligned with school and TVET curricula have also been developed.</p>
                        </div>
                        <div className="kk-learning-audiences">
                            {learningAudiences.map((audience, index) => (
                                <div key={index} className="kk-audience-card">
                                    <FontAwesomeIcon icon={audience.icon} className="kk-audience-icon" />
                                    <h3>{audience.title}</h3>
                                    <p>{audience.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="kk-cta-center">
                        <button className="kk-primary-btn" onClick={() => window.location.href = '/contact-location'}>
                            Enquire About Learning Visits
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Technology Section */}
            <div className="kk-section kk-hidden">
                <div className="kk-container">
                    <h1>Where Agriculture Meets Technology</h1>
                    <div className="kk-technology-content">
                        <div className="kk-technology-text">
                            <p>Kinda Katch uses KindaTech ERP and production records to support monitoring and management of farm operations.</p>
                            <p>The system records:</p>
                            <ul>
                                <li>Fish feeding activities</li>
                                <li>Fish growth and weight checks</li>
                                <li>Farm inputs</li>
                                <li>Operational expenses</li>
                                <li>Production costs</li>
                                <li>Farm activities and observations</li>
                            </ul>
                        </div>
                        <div className="kk-technology-image">
                            <img src="/images/kinda-katch-stocking.jpg" alt="Digital farm management in action" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Community Section */}
            <div className="kk-section kk-alt-bg kk-hidden">
                <div className="kk-container">
                    <h1>Built Locally, Growing Together</h1>
                    <div className="kk-community-content">
                        <div className="kk-community-text">
                            <p>Kinda Katch brings together local implementation, youth participation, practical agricultural knowledge and Signifide Group International's digital systems capabilities.</p>
                            <p>Technical support from Gatsby Africa's aquaculture specialists has informed farm planning and fish husbandry.</p>
                            <p>The initiative aims to learn with surrounding communities and adapt future approaches to local priorities.</p>
                            <button className="kk-primary-btn" onClick={() => window.location.href = '/contact-location'}>
                                Explore Partnership Opportunities
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>
                        <div className="kk-community-image">
                            <img src="/images/kinda-katch-team.jpg" alt="Kinda Katch team working together" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Roadmap Section */}
            <div className="kk-section kk-hidden">
                <div className="kk-container">
                    <h1>Growing the Future</h1>
                    <p className="kk-section-subtitle">Ongoing and planned development activities</p>
                    <div className="kk-roadmap">
                        {roadmapItems.map((item, index) => (
                            <div key={index} className="kk-roadmap-item">
                                <div className="kk-roadmap-number">{index + 1}</div>
                                <div className="kk-roadmap-content">
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="kk-cta-section" style={{ backgroundImage: "url('/images/kinda-katch-hero.jpg')" }}>
                <div className="kk-cta-overlay">
                    <div className="kk-container">
                        <h1>Let's Grow a More Resilient Future Together</h1>
                        <p>Whether you're an educational institution, technical partner, community organization, potential buyer or sustainability advocate, Kinda Katch welcomes opportunities to learn, collaborate and grow.</p>
                        <div className="kk-cta-buttons">
                            <button className="kk-primary-btn-light" onClick={() => window.location.href = '/contact-location'}>
                                Partner With Us
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                            <button className="kk-secondary-btn-light" onClick={() => window.location.href = '/contact-location'}>
                                Contact Signifide Group
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <Footer />

            {/* Lightbox */}
            {lightboxImage && (
                <div className="kk-lightbox" onClick={closeLightbox}>
                    <button className="kk-lightbox-close" onClick={closeLightbox}>&times;</button>
                    <img src={lightboxImage} alt="Gallery image" />
                </div>
            )}
        </div>
    );
}

export default KindaKatch;
